<script setup>
import { computed } from 'vue';

const props = defineProps({
  label: { type: String, required: true },
  value: { type: String, required: true },
  trend: { type: String, default: '' },
  hint: { type: String, default: '' },
  // null = neutral, true = good direction, false = bad direction
  trendGood: { type: Boolean, default: null },
  clickable: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['click']);

// The delta reads as a tinted pill beside the value, so a neutral trend still
// sits on the same baseline as a good or bad one.
const trendClass = computed(() => {
  if (props.trendGood === null) return 'bg-n-slate-3 text-n-slate-11';
  return props.trendGood
    ? 'bg-n-teal-3 text-n-teal-11'
    : 'bg-n-ruby-3 text-n-ruby-11';
});

const onActivate = () => {
  if (props.clickable) emit('click');
};
</script>

<template>
  <div
    class="flex flex-col gap-2 p-5 border group rounded-xl bg-n-solid-1 border-n-weak"
    :class="
      clickable
        ? 'cursor-pointer transition-all hover:border-n-violet-7 hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand-violet'
        : ''
    "
    :role="clickable ? 'button' : undefined"
    :tabindex="clickable ? 0 : undefined"
    @click="onActivate"
    @keydown.enter.self.prevent="onActivate"
    @keydown.space.self.prevent="onActivate"
  >
    <div class="flex items-center gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{ label }}</span>
      <span
        v-if="hint"
        v-tooltip="hint"
        class="transition-opacity opacity-0 cursor-help i-lucide-info size-3.5 text-n-slate-10 group-hover:opacity-100"
      />
    </div>
    <div v-if="loading" class="flex items-end gap-2">
      <div class="w-20 h-8 rounded bg-n-slate-3 animate-pulse" />
      <div class="w-12 h-5 mb-1 rounded-full bg-n-slate-3 animate-pulse" />
    </div>
    <div v-else class="flex items-end gap-2">
      <span
        class="text-2xl font-semibold tracking-tight tabular-nums text-n-slate-12"
      >
        {{ value }}
      </span>
      <span
        v-if="trend"
        class="inline-flex items-center h-5 px-1.5 mb-1 text-xs font-medium rounded-full tabular-nums"
        :class="trendClass"
      >
        {{ trend }}
      </span>
    </div>
  </div>
</template>
